import { useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { useAuthStore } from '@/store/auth-store'
import { onboardingComplete } from '@/features/onboarding/utils/onboarding'
import { PageLoader } from '@/shared/components/page-loader'

// Single source of truth for route protection, shared by the main shell and the
// focus mode shell so the two can never drift apart.
export function AuthGuard({ children }: { children: ReactNode }) {
  const { status, profile, initialize } = useAuthStore()
  const location = useLocation()

  useEffect(() => {
    initialize()
  }, [initialize])

  if (status === 'loading') {
    return <PageLoader />
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />
  }

  if (status === 'no-profile') {
    return <Navigate to="/signup" replace />
  }

  if (status === 'inactive') {
    return <Navigate to="/account-review" replace />
  }

  if (status === 'wrong-role') {
    return <Navigate to="/access-denied" replace />
  }

  // A connector who registered on the mobile app has not supplied what the web
  // platform needs. Hold them on the onboarding page until the loop is closed, rather
  // than letting them reach screens that would refuse to work.
  if (profile && !onboardingComplete(profile) && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  return <>{children}</>
}
